import { VIEW_H_PX, VIEW_W_PX } from '../shared/constants.ts';

const DISPLAY_FONT = '"Lilita One", Rubik, system-ui, sans-serif';

// Parallax: how fast each layer scrolls compared to the level.
const TOWERS_P = 0.12;
const STANDS_P = 0.3;
const BOARDS_P = 0.6;

const TOWER_GAP = 300;
const SEAT_W = 8;
const ROW_H = 12;
const STANDS_TOP = 84;
const ROWS = 6;
const BOARD_W = 118;
const BOARD_Y = 166;
const BOARD_H = 18;
const FIELD_TOP = BOARD_Y + BOARD_H;
/** Where the floodlights land: on the field, just behind the track. */
const POOL_Y = FIELD_TOP + 16;

/** Ad boards around the track. `#` is replaced by the live watcher count. */
const BOARDS = [
  'GLOPE', '# WATCHING', "DON'T FALL", 'NO REFUNDS', 'CHAT IS EVIL', 'JUMP!', 'BE THE CONTENT', 'SKILL ISSUE?', 'RUN', 'GG',
  'HI MOM', 'NO PRESSURE', 'WAIT YOUR TURN', 'CLIP IT', 'TOUCH GRASS', 'HYDRATE', 'NO CHECKPOINTS', 'KEEP RIGHT',
  'THE VOID IS HUNGRY', 'ONE MORE RUN', 'NOT A BUG', 'TOTALLY FAIR', 'BREATHE', 'CHAT DECIDES', 'NO PAUSE BUTTON',
  'SPIKES: SHARP', 'GRAVITY: ON', 'FAIL FASTER', 'LAG IS A MYTH', 'SAY HI IN CHAT', 'NICE TRY', 'ALMOST!', 'BIG IF TRUE',
  'SPONSORED BY NOBODY', 'WORLD RECORD?', 'WE SAW THAT', 'GLOPE', '# WATCHING',
];
/** Bright text on its own deep tint, so every board reads. */
const BOARD_COLORS: [string, string][] = [
  ['#5ef2a0', '#08261a'], ['#ffd23f', '#2a2008'], ['#ff6a88', '#2c0a14'], ['#7cc6ff', '#0a1a30'], ['#ffa552', '#2c1606'], ['#c4b6ff', '#1a1236'],
];

interface Flash {
  x: number;
  y: number;
  at: number;
}

/**
 * The stadium behind the level: floodlights, stands whose crowd grows with
 * the real number of watchers (and gets excited when chat is about to strike,
 * when a sabotage hits, and when the runner splats), and ad boards along the
 * track. Cosmetic only; seats are hashed from their index so the crowd stays
 * put as it scrolls.
 */
export class Backdrop {
  private hype = 0.3;
  private flashes: Flash[] = [];

  /** Something happened: the crowd goes wild for a moment (0..1). */
  cheer(amount: number): void {
    this.hype = Math.max(this.hype, amount);
  }

  draw(ctx: CanvasRenderingContext2D, cam: number, hue: number, now: number, dt: number, chaos: number, watchers: number): void {
    const base = 0.2 + 0.6 * Math.min(1, chaos);
    this.hype += (base - this.hype) * Math.min(1, dt * 0.7);

    const sky = ctx.createLinearGradient(0, 0, 0, VIEW_H_PX);
    sky.addColorStop(0, `hsl(${hue}, 45%, 7%)`);
    sky.addColorStop(0.45, `hsl(${hue + 15}, 45%, 14%)`);
    sky.addColorStop(1, `hsl(${hue + 25}, 45%, 20%)`);
    ctx.fillStyle = sky;
    ctx.fillRect(-10, -10, VIEW_W_PX + 20, VIEW_H_PX + 20);

    const towers = cam * TOWERS_P;
    this.towers(ctx, towers);
    this.stands(ctx, cam * STANDS_P, hue, now, dt, watchers);
    this.field(ctx, cam * BOARDS_P, hue);
    this.boards(ctx, cam * BOARDS_P, now, watchers);
    this.beams(ctx, towers, now);
  }

  private eachTower(off: number, fn: (x: number, top: number, i: number) => void): void {
    const first = Math.floor((off - 160) / TOWER_GAP);
    for (let i = first; i * TOWER_GAP - off < VIEW_W_PX + 160; i++) fn(i * TOWER_GAP + 140 - off, 44 + (hash(i, 7) % 8), i); // below the HUD
  }

  /** The masts and light banks stand behind the stands… */
  private towers(ctx: CanvasRenderingContext2D, off: number): void {
    this.eachTower(off, (x, top) => {
      ctx.fillStyle = 'rgba(8, 6, 18, 0.9)';
      ctx.fillRect(x - 1.5, top + 10, 3, STANDS_TOP - top);
      ctx.fillStyle = '#1a1630';
      ctx.fillRect(x - 10, top, 20, 10);
      ctx.fillStyle = '#fff6da';
      for (let r = 0; r < 2; r++) for (let c = 0; c < 4; c++) ctx.fillRect(x - 8.5 + c * 4.5, top + 1.5 + r * 4.2, 3, 2.8);
    });
  }

  /** …and their light falls across the crowd onto the field, in front of everything. */
  private beams(ctx: CanvasRenderingContext2D, off: number, now: number): void {
    ctx.globalCompositeOperation = 'lighter';
    this.eachTower(off, (x, top, i) => {
      for (let b = 0; b < 2; b++) {
        const sway = Math.sin(now / (2600 + b * 900) + i * 1.7 + b) * 70;
        const bx = x + (b ? 40 : -40) + sway;
        const g = ctx.createLinearGradient(0, top, 0, POOL_Y);
        g.addColorStop(0, 'rgba(255, 244, 210, 0.16)');
        g.addColorStop(1, 'rgba(255, 244, 210, 0.07)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.moveTo(x - 6, top + 5);
        ctx.lineTo(x + 6, top + 5);
        ctx.lineTo(bx + 30, POOL_Y);
        ctx.lineTo(bx - 30, POOL_Y);
        ctx.fill();
        // The pool of light where it lands.
        ctx.save();
        ctx.translate(bx, POOL_Y);
        ctx.scale(1, 0.25);
        const pool = ctx.createRadialGradient(0, 0, 0, 0, 0, 40);
        pool.addColorStop(0, 'rgba(255, 244, 210, 0.22)');
        pool.addColorStop(1, 'rgba(255, 244, 210, 0)');
        ctx.fillStyle = pool;
        ctx.fillRect(-40, -40, 80, 80);
        ctx.restore();
      }
      const glow = ctx.createRadialGradient(x, top + 5, 0, x, top + 5, 34);
      glow.addColorStop(0, 'rgba(255, 240, 200, 0.45)');
      glow.addColorStop(1, 'rgba(255, 240, 200, 0)');
      ctx.fillStyle = glow;
      ctx.fillRect(x - 34, top - 29, 68, 68);
    });
    ctx.globalCompositeOperation = 'source-over';
  }

  /** The pitch, past the ad boards: mowed stripes in perspective, fading into the dark (the pits are deep). */
  private field(ctx: CanvasRenderingContext2D, off: number, hue: number): void {
    ctx.fillStyle = '#1d5a34';
    ctx.fillRect(-10, FIELD_TOP, VIEW_W_PX + 20, VIEW_H_PX - FIELD_TOP + 10);
    // Stripes lean outwards from the middle of the screen, like a camera looking across the pitch.
    ctx.fillStyle = '#236a3d';
    const w = 40;
    const s0 = Math.floor((off - 200) / (w * 2));
    for (let s = s0; (s * w * 2) - off < VIEW_W_PX + 200; s++) {
      const x = s * w * 2 - off;
      const lean = (x + w / 2 - VIEW_W_PX / 2) * 0.6;
      ctx.beginPath();
      ctx.moveTo(x, FIELD_TOP);
      ctx.lineTo(x + w, FIELD_TOP);
      ctx.lineTo(x + w + lean, VIEW_H_PX);
      ctx.lineTo(x + lean, VIEW_H_PX);
      ctx.fill();
    }
    // A painted line along the near edge of the track.
    ctx.fillStyle = 'rgba(255, 255, 255, 0.35)';
    ctx.fillRect(-10, FIELD_TOP + 9, VIEW_W_PX + 20, 1);
    // Night falls on it like on everything else, then the abyss.
    const g = ctx.createLinearGradient(0, FIELD_TOP, 0, VIEW_H_PX);
    g.addColorStop(0, `hsla(${hue}, 45%, 12%, 0.35)`);
    g.addColorStop(0.45, `hsla(${hue}, 45%, 8%, 0.75)`);
    g.addColorStop(1, `hsla(${hue}, 45%, 4%, 0.97)`);
    ctx.fillStyle = g;
    ctx.fillRect(-10, FIELD_TOP, VIEW_W_PX + 20, VIEW_H_PX - FIELD_TOP + 10);
  }

  private stands(ctx: CanvasRenderingContext2D, off: number, hue: number, now: number, dt: number, watchers: number): void {
    // The structure: a roof edge with little lights, then the tiers.
    const bottom = STANDS_TOP + ROWS * ROW_H + 8;
    ctx.fillStyle = `hsl(${hue + 10}, 30%, 14%)`;
    ctx.fillRect(-10, STANDS_TOP - 8, VIEW_W_PX + 20, bottom - STANDS_TOP + 8);
    ctx.fillStyle = `hsl(${hue + 10}, 30%, 7%)`;
    ctx.fillRect(-10, STANDS_TOP - 10, VIEW_W_PX + 20, 4);
    for (let r = 0; r < ROWS; r++) {
      ctx.fillStyle = `hsl(${hue + 10}, 28%, ${11 + r * 1.6}%)`;
      ctx.fillRect(-10, STANDS_TOP + r * ROW_H + 8, VIEW_W_PX + 20, 2);
    }

    // The crowd: more seats taken the more people are really watching.
    const full = 0.2 + 0.8 * Math.min(1, Math.log10(1 + watchers) / Math.log10(500));
    const hype = this.hype;
    for (let r = 0; r < ROWS; r++) {
      const y = STANDS_TOP + r * ROW_H + 6;
      const shift = (r % 2) * SEAT_W * 0.5;
      const i0 = Math.floor((off - shift) / SEAT_W) - 1;
      const i1 = i0 + Math.ceil(VIEW_W_PX / SEAT_W) + 2;
      for (let i = i0; i <= i1; i++) {
        const h = hash(i, r);
        if ((h % 1000) / 1000 >= full) continue;
        const x = i * SEAT_W + shift - off + 4;
        const excitable = (h >>> 10) % 3 !== 0;
        const jump = excitable ? Math.max(0, Math.sin(now / (110 + (h % 60)) + (h % 17))) * hype * 3 : 0;
        const py = y - jump;
        const shirtHue = (h >>> 4) % 360;
        const light = 22 + r * 2.2;
        ctx.fillStyle = `hsl(${shirtHue}, 32%, ${light}%)`;
        ctx.beginPath();
        ctx.roundRect(x - 3, py, 6, 6, [2.5, 2.5, 0, 0]);
        ctx.fill();
        ctx.fillStyle = `hsl(${(h >>> 6) % 30 + 15}, 30%, ${light - 2 + ((h >>> 14) % 3) * 4}%)`;
        ctx.beginPath();
        ctx.arc(x, py - 2, 2.3, 0, Math.PI * 2);
        ctx.fill();
        // Arms up when it gets exciting.
        if (excitable && hype > 0.55 && (h >>> 3) % 2) {
          ctx.strokeStyle = `hsl(${shirtHue}, 32%, ${light}%)`;
          ctx.lineWidth = 1.3;
          ctx.beginPath();
          ctx.moveTo(x - 2.6, py + 1);
          ctx.lineTo(x - 3.6, py - 4);
          ctx.moveTo(x + 2.6, py + 1);
          ctx.lineTo(x + 3.6, py - 4);
          ctx.stroke();
        }
        // Some are filming.
        if ((h >>> 12) % 6 === 0) {
          ctx.fillStyle = 'rgba(200, 225, 255, 0.6)';
          ctx.fillRect(x + 1.6, py - 6.5, 1.6, 2.4);
          if (Math.random() < dt * hype * 0.5) this.flashes.push({ x: x + off + 2.4, y: py - 5.3, at: now });
        }
      }
    }
    // A haze over the crowd keeps it behind the action (the runner must always pop).
    ctx.fillStyle = `hsla(${hue + 10}, 35%, 10%, 0.22)`;
    ctx.fillRect(-10, STANDS_TOP - 8, VIEW_W_PX + 20, bottom - STANDS_TOP + 8);
    // Camera flashes.
    this.flashes = this.flashes.filter((f) => now - f.at < 160);
    ctx.globalCompositeOperation = 'lighter';
    for (const f of this.flashes) {
      const t = 1 - (now - f.at) / 160;
      const x = f.x - off;
      const g = ctx.createRadialGradient(x, f.y, 0, x, f.y, 7 * t + 2);
      g.addColorStop(0, `rgba(255, 255, 255, ${0.6 * t})`);
      g.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = g;
      ctx.fillRect(x - 12, f.y - 12, 24, 24);
    }
    ctx.globalCompositeOperation = 'source-over';
  }

  private boards(ctx: CanvasRenderingContext2D, off: number, now: number, watchers: number): void {
    const step = BOARD_W + 4;
    const i0 = Math.floor(off / step) - 1;
    ctx.textAlign = 'center';
    for (let i = i0; i * step - off < VIEW_W_PX; i++) {
      const x = i * step - off;
      const h = hash(i, 99);
      // LED boards flick to a new message (and colour) every few seconds.
      const t = now / 1000 + (h % 50) / 7;
      // (Unsigned shifts: a negative index would leave the previous fill colour, dark on dark.)
      const n = (h >>> 3) + Math.floor(t / 9);
      const [color, tint] = BOARD_COLORS[(n * 5 + (h >>> 7)) % BOARD_COLORS.length];
      ctx.fillStyle = tint;
      ctx.fillRect(x, BOARD_Y, BOARD_W, BOARD_H);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.12)';
      ctx.fillRect(x, BOARD_Y, BOARD_W, 1);
      if (t % 9 < 0.12) continue;
      const msg = BOARDS[n % BOARDS.length].replace('#', watchers.toLocaleString());
      let size = 11;
      ctx.font = `${size}px ${DISPLAY_FONT}`;
      const tw = ctx.measureText(msg).width;
      if (tw > BOARD_W - 10) ctx.font = `${(size = (size * (BOARD_W - 10)) / tw)}px ${DISPLAY_FONT}`;
      ctx.fillStyle = color;
      ctx.fillText(msg, x + BOARD_W / 2, BOARD_Y + 9 + size * 0.36);
      // LED rows.
      ctx.fillStyle = 'rgba(0, 0, 0, 0.22)';
      for (let y = BOARD_Y + 2; y < BOARD_Y + BOARD_H; y += 2) ctx.fillRect(x, y, BOARD_W, 0.7);
    }
    // A strip of shadow where the boards meet the track.
    ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
    ctx.fillRect(-10, BOARD_Y + BOARD_H, VIEW_W_PX + 20, 3);
  }
}

/** Small integer hash, stable per seat / board / tower. */
function hash(a: number, b: number): number {
  let h = Math.imul(a ^ 0x9e3779b9, 0x85ebca6b) ^ Math.imul(b + 0x632be5ab, 0xc2b2ae35);
  h ^= h >>> 15;
  h = Math.imul(h, 0x2c1b3c6d);
  h ^= h >>> 12;
  return h >>> 0;
}
